import { createSelector } from 'reselect';

import { isCyberSourceRequired } from '@commons/utils/payment/isCyberSourceRequired';

import getCart from '@core/reducers/cart/selectors/getCart';
import getShop from '@core/reducers/shop/selectors/getShop';
import getShopPaymentMethods from '@core/reducers/shop/selectors/getShopPaymentMethods';
import { getCountryShortName } from '@shared/selectors';

import getPaymentMethod from './selectors/getPaymentMethod';
import { MODULE_NAME } from './constants';
import getShopOfflinePaymentMethods from './selectors/getShopOfflinePaymentMethods';
import getPaymentGatewayTypeByCountry from './selectors/getPaymentGatewayTypeByCountry';

const getPropFromModule = (state, ...props) =>
  state.getIn([MODULE_NAME, ...props]);

export const getIsMandatoryPaymentAmount = createSelector(
  getShop,
  shop => shop?.mandatoryPaymentAmount || false,
);

export const getIsSendingPaymentMethod = state =>
  getPropFromModule(state, 'isSendingPaymentMethod');

export const getSelectedPaymentMethod = createSelector(
  state => getPropFromModule(state, 'selectedPayment'),
  selectedPayment => selectedPayment?.toJS(),
);

export const getDraftPayments = createSelector(
  state => getPropFromModule(state, 'draftPayments'),
  selectedPayment => selectedPayment,
);

export const getConfirmedPaymentMethod = state =>
  getPropFromModule(state, 'confirmedPayment')?.toJS() ||
  getCart(state)?.paymentMethod;

// only for tracking
export const getShopPaymentMethodSelected = state => {
  try {
    const confirmedPaymentMethod = getConfirmedPaymentMethod(state);
    const shopPaymentMethods = getShopPaymentMethods(state);
    if (!confirmedPaymentMethod || !shopPaymentMethods) return null;

    const { id } = confirmedPaymentMethod;
    return shopPaymentMethods.find(x => x.id === id);
  } catch (error) {
    return null;
  }
};

export const getSelectedDraft = createSelector(
  getSelectedPaymentMethod,
  getDraftPayments,
  getConfirmedPaymentMethod,
  (selectedPayment, draftPayments, confirmedPayment) => {
    if (!selectedPayment && !confirmedPayment) return null;
    const selection = selectedPayment || confirmedPayment;
    // if there is a draft payment with data,
    // enhance the selected payment with the draft data
    const draft = draftPayments.get(selection.id)?.toJS();
    if (draft && selection) return { ...draft, id: selection.id };

    return selection;
  },
);

export const getPaymentMethodFromParams = createSelector(
  getShopOfflinePaymentMethods,
  (s, paymentId) => paymentId,
  (paymentMethods, paymentId) => {
    if (paymentMethods && paymentId) {
      const paymentMethod = paymentMethods.find(
        pm => pm.id.toString() === paymentId,
      );

      if (!paymentMethod) return null;
      return paymentMethod;
    }

    return null;
  },
);

export const getAvailableOnlinePayments = createSelector(
  state => getPropFromModule(state, 'onlinePaymentsAvailable'),
  onlinePaymentsAvailable => onlinePaymentsAvailable?.toJS(),
);

export const getDummyPayment = createSelector(
  state => getPropFromModule(state, 'dummyPayment'),
  dummyPayment => dummyPayment?.toJS(),
);

export const getOnlinePaymentErrorState = createSelector(
  state => getPropFromModule(state, 'onlinePaymentError'),
  onlinePaymentError => onlinePaymentError?.toJS(),
);

export const getShowOnlinePaymentError = state => {
  const onlinePaymentState = getOnlinePaymentErrorState(state);
  return Boolean(onlinePaymentState?.show);
};

export const getPaymentError = state => {
  const onlinePaymentState = getOnlinePaymentErrorState(state);
  return onlinePaymentState?.error;
};

export const getIsFetchingOnlinePaymentMethods = state =>
  getPropFromModule(state, 'isFetchingOnlinePaymentMethods');

export const getHasOnlinePaymentMethod = createSelector(
  getPaymentMethod,
  paymentMethod => !!paymentMethod,
);

export const getLoadCyberSource = createSelector(
  getPaymentGatewayTypeByCountry,
  getCountryShortName,
  (type, countryShortName) => isCyberSourceRequired(type, countryShortName),
);

export const getIsOnlinePaymentMethodNew = createSelector(
  getConfirmedPaymentMethod,
  paymentMethod => paymentMethod?.isNew,
);

export const getPaymentMethodsDisabled = createSelector(
  state => getPropFromModule(state, 'paymentMethodsDisabled'),
  methodsDisabled => methodsDisabled?.toJS(),
);

export const getCreditCardsDiscountCampaigns = createSelector(
  state => getPropFromModule(state, 'creditCardsDiscountCampaigns'),
  creditCardsDiscountCampaigns => creditCardsDiscountCampaigns?.toJS() || {},
);

export const getIsFetchingCreditCardsDiscountCampaigns = state =>
  getPropFromModule(state, 'isFetchingCreditCardsDiscountCampaigns');

export const getErrorsQuantityOnlinePayment = state =>
  getPropFromModule(state, 'errorsQuantityOnlinePayment');

export const getPaymentMethodOrigin = state =>
  getPropFromModule(state, 'paymentMethodOrigin');
